<template>
  <b-media :class="['user-media', { ['one-line']: oneLine, 'user-media--small': small }]" data-cy="user-media">
    <template #aside>
      <div class="user-media__avatar">
        <avatar-image
          v-if="!isAnonymous"
          :size="computedAvatarSize"
          v-bind="{
            showPresence,
            userLink,
            user,
            squareAvatar,
          }"
        />
        <avatar-image v-else :size="computedAvatarSize" v-bind="{ showPresence, squareAvatar }" />
      </div>
    </template>

    <template v-if="isAnonymous">
      <span class="user-media__fullname">Anonymous</span>
    </template>

    <div v-else class="user-media__body-inner-wrapper">
      <div class="user-media__title-wrapper">
        <span :class="{ 'text-break': !minimal }" class="user-media__fullname" v-b-tooltip.hover :title="fullName">
          <!-- NOTE: Shouldn't academic degree be hidden on minimal: true? -->
          <span v-if="user?.academicDegree && showDegree" class="user-media__academic-degree">
            {{ user?.academicDegree }}
          </span>
          {{ fullName }}
        </span>
        <user-media-verification v-if="verification" v-bind="{ verification, user }" />
      </div>

      <div
        v-if="!oneLine && !minimal && showInstitution && institutions.length"
        class="d-flex flex-column user-media__institution-wrapper"
      >
        <span v-for="(institution, i) in institutions" :key="i" class="user-media__institution">
          <fragment v-if="!maxInstitutionsShown">
            {{ institution?.name }}{{ i !== institutions.length - 1 ? ', ' : '' }}
          </fragment>
          <fragment v-else-if="i <= maxInstitutionsShown - 1">
            {{ institution?.name }}{{ i !== maxInstitutionsShown - 1 && i !== institutions.length - 1 ? ', ' : '' }}
          </fragment>
        </span>
      </div>

      <span
        v-else-if="showInstitution && !minimal && institutions.length"
        :title="institutions.map(({ name }) => name).join(', ')"
        class="user-media__institutions"
      >
        {{
          maxInstitutionsShown
            ? institutions
                .slice(0, maxInstitutionsShown)
                .map(({ name }) => name)
                .join(', ')
            : institutions.map(({ name }) => name).join(', ')
        }}
      </span>

      <component
        :is="noLink ? 'span' : 'profile-link'"
        v-if="!isResearch && usernameString"
        class="user-media__username"
        v-bind="{ user }"
        :data-cy="`user-media-username${noLink ? '' : '-link'}`"
      >
        {{ usernameString }}
      </component>

      <component
        :is="noLink ? 'span' : 'profile-link'"
        v-else
        class="user-media__email"
        v-bind="{ user }"
        :data-cy="`user-media-email${noLink ? '' : '-link'}`"
      >
        {{ user?.email }}
      </component>

      <follow-user-button
        v-if="withFollowButton && authenticated && !isAuthUser"
        class="mt-2 follow-btn"
        :target-user="user"
        size="sm"
        @unfollow="$emit('unfollow')"
        @follow="$emit('follow')"
      />
    </div>

    <slot name="addons"></slot>
  </b-media>
</template>

<script>
  import { getUserFullName } from '@/utils/entities/User';

  export default {
    name: 'UserMedia',

    components: {
      UserMediaVerification: () => import('@/Atoms/UserMediaVerification'),
      FollowUserButton: () => import('./FollowUserButton'),
      AvatarImage: () => import('@/Atoms/AvatarImage'),
      ProfileLink: () => import('@/Atoms/ProfileLink'),
    },

    props: {
      showInstitution: { type: Boolean, default: true },
      showDegree: { type: Boolean, default: true },
      onlyPrimaryInstitution: Boolean,
      maxInstitutionsShown: Number,
      withFollowButton: Boolean,
      itemWrapperClass: String,
      fullnameClass: String,
      showPresence: Boolean,
      isAnonymous: Boolean,
      verification: Object,
      isVerified: Boolean,
      userLink: Boolean,
      minimal: Boolean,
      oneLine: Boolean, // displays all texts in maximum one line. Uses ellipsis and titles. Should be made true by default
      noLink: Boolean,
      small: Boolean,
      user: Object,
      avatarSize: {
        type: Number,
        default: 40,
      },
      squareAvatar: Boolean,
    },

    computed: {
      institutions() {
        return this.user.institutions.filter(({ isPrimary }) => !this.onlyPrimaryInstitution || isPrimary);
      },

      isAuthUser() {
        return this.user.id === this.$store.state.currentUser.id;
      },

      authenticated() {
        return this.$store.getters.authenticated;
      },

      fullName() {
        return getUserFullName(this.user);
      },

      usernameString() {
        return this.user.username ? `@${this.user.username}` : '';
      },

      // NOTE: this is a tweak to save time. We should use a better way to detect if we are on a research page in the SPA
      isResearch() {
        return this.$route().current().startsWith('research-');
      },

      computedAvatarSize() {
        return this.small ? 30 : this.avatarSize;
      },
    },
  };
</script>
